/**
 * @file repOrderRequests.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-29
 * @modified 2026-10-30
 * @description Rep order requests (Cam, 29 Oct 2026): a rep asks the office
 *              for an order, a standing order, or a change to one of either,
 *              from their dashboard ("Request an order") or from the order /
 *              standing order ("Request a change"). The office opens it from
 *              Sales Orders' "Rep requests", makes the order (or standing
 *              order) from it, and saving that completes the request in the
 *              same transaction. Pure: the server loads the facts
 *              (services/repOrderRequestService.js), these decide and word
 *              them. Client source; utils/repOrderRequests.js on the server is
 *              GENERATED from it (node scripts/sync-shared-utils.js).
 *
 *   A REQUEST
 *     kind      order | standing_order | amend_order | amend_standing_order
 *     lines     [{ item_id | text, quantity, unit_weight_kg (0 = None — no
 *               weight; required, units are required on orders), additives,
 *               note }] — a standing order has ONE product line (its one
 *               quantity), an order up to MAX_LINES
 *     delivery  an order's: { type: 'date', date } or { type: 'range', from, to }
 *               (when it is not needed on one day) — never in the past, except
 *               an amendment keeping the order's own date
 *     schedule  a standing order's: weekly on a weekday (Mon–Fri, as the
 *               standing order screen offers) or every N days / weeks / months
 *     notes
 *
 *   ITS LIFE
 *     open (Sent) → opened (Opened by <who> at <when> — the first opener is
 *     kept) → completed (Done — SO-D…, when the order saved from it); or
 *     closed by the office with a reason; or withdrawn by the rep while still
 *     open (once the office has opened it, it is theirs).
 */

import { formatDateTimeLondon, formatTimeLondon } from './dateFormatters';
import { formatCount, formatQuantity } from './numberFormatters';

export const REQUEST_KINDS = ['order', 'standing_order', 'amend_order', 'amend_standing_order'];
export const STANDING_KINDS = ['standing_order', 'amend_standing_order'];
export const AMEND_KINDS = ['amend_order', 'amend_standing_order'];
export const OPEN_STATUSES = ['open', 'opened'];
export const DONE_STATUSES = ['completed', 'closed', 'withdrawn'];

/** What each kind is called, in a sentence and on a card. */
export const KIND_WORDS = {
  order: 'Order',
  standing_order: 'Standing order',
  amend_order: 'Change to an order',
  amend_standing_order: 'Change to a standing order'
};

/**
 * What the office needs to act on each kind — open it, close it, and the save
 * that completes it (the order create / edit, the standing order save).
 */
export const OFFICE_PERMISSION = {
  order: 'orders.create',
  amend_order: 'orders.edit',
  standing_order: 'standing_orders.edit',
  amend_standing_order: 'standing_orders.edit'
};

/** The save's refusal codes. */
export const NOT_READY_CODE = 'REP_REQUESTS_NOT_READY';
export const NOT_READY_TEXT = 'Not ready — migration rep_order_requests.sql has not run';

export const MAX_LINES = 20;
export const MAX_ADDITIVES = 10;
export const NOTES_MAX = 2000;
export const LINE_TEXT_MAX = 200;
export const LINE_NOTE_MAX = 500;
export const CLOSE_REASON_MAX = 500;
/** A delivery window wider than this is not a delivery date any more. */
export const RANGE_MAX_DAYS = 92;
export const INTERVAL_MAX = 365;

/**
 * The units a rep may say a line is in — the order grid's own fixed units,
 * and None for a charge with no weight. ("Other — by the pallet" needs a
 * pallet weight and count; the office sets that on the order.)
 */
export const REQUEST_UNIT_CHOICES = [
  { value: '1000', label: 'Bulk — per tonne' },
  { value: '500', label: 'Tote (500kg)' },
  { value: '25', label: '25kg bag' },
  { value: '20', label: '20kg bag' },
  { value: '0', label: 'None — no weight' }
];

/** Weekly standing orders run on a working day, as the standing order screen offers. */
export const WEEKDAYS = [
  { value: 1, label: 'Monday', plural: 'Mondays' },
  { value: 2, label: 'Tuesday', plural: 'Tuesdays' },
  { value: 3, label: 'Wednesday', plural: 'Wednesdays' },
  { value: 4, label: 'Thursday', plural: 'Thursdays' },
  { value: 5, label: 'Friday', plural: 'Fridays' }
];

export const INTERVAL_TYPES = [
  { value: 'days', label: 'Days', one: 'day' },
  { value: 'weeks', label: 'Weeks', one: 'week' },
  { value: 'months', label: 'Months', one: 'month' }
];

export const isStandingKind = (kind) => STANDING_KINDS.includes(kind);
export const isAmendKind = (kind) => AMEND_KINDS.includes(kind);
export const isOpenStatus = (status) => OPEN_STATUSES.includes(status);

const blank = (v) => v === null || v === undefined || String(v).trim() === '';
const int = (v) => {
  if (blank(v)) return null;
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : null;
};
const trimTo = (v, max) => {
  if (blank(v)) return '';
  return String(v).trim().slice(0, max);
};

/** 'YYYY-MM-DD' that is a real calendar day. */
export function isIsoDay(s) {
  if (typeof s !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

/** Today in London, 'YYYY-MM-DD'. */
export function londonToday(now = new Date()) {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

const daysBetween = (a, b) => {
  const [y1, m1, d1] = a.split('-').map(Number);
  const [y2, m2, d2] = b.split('-').map(Number);
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86400000);
};

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** "Mon 03/11/2026" — a calendar day, never through a local Date. */
export function dayText(iso) {
  if (!isIsoDay(iso)) return '';
  const [y, m, d] = iso.split('-').map(Number);
  const wd = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return `${WEEKDAY_SHORT[wd]} ${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
}

/** "T", "25kg bag", "500kg tote", "no weight", "15.873kg" — '' when unknown. */
export function unitWords(kg) {
  if (blank(kg)) return '';
  const n = Number(kg);
  if (!Number.isFinite(n) || n < 0) return '';
  if (n === 0) return 'no weight';
  if (n === 1000) return 'T';
  if (n === 500) return '500kg tote';
  if (n === 25) return '25kg bag';
  if (n === 20) return '20kg bag';
  return `${Number(n.toFixed(3))}kg`;
}

/**
 * The unit choices for a line: the fixed ones, and the item's own weight when
 * it is none of them (an item in 15.873kg packs keeps its unit).
 */
export function unitChoicesFor(itemKg) {
  const n = Number(itemKg);
  if (blank(itemKg) || !Number.isFinite(n) || n <= 0) return REQUEST_UNIT_CHOICES;
  const value = String(n);
  if (REQUEST_UNIT_CHOICES.some(o => o.value === value)) return REQUEST_UNIT_CHOICES;
  return [...REQUEST_UNIT_CHOICES.slice(0, 4), { value, label: `${Number(n.toFixed(3))}kg (the item's)` }, REQUEST_UNIT_CHOICES[4]];
}

/** "20.00 T", "40 × 25kg bag", "3 (no weight)", "5" (unit not known). */
export function quantityText(quantity, kg) {
  const q = Number(quantity);
  if (!Number.isFinite(q)) return '';
  const unit = unitWords(kg);
  if (unit === 'T') return `${formatQuantity(q)} T`;
  if (unit === 'no weight') return `${formatCount(q)} (no weight)`;
  if (unit) return `${formatCount(q)} × ${unit}`;
  return formatCount(q);
}

/** The product a line names: "10014208 Harvester 18", or what the rep typed. */
export function lineName(line) {
  if (!line) return '';
  const code = String(line.item_code || '').trim();
  const name = String(line.item_name || '').trim();
  if (code || name) return [code, name].filter(Boolean).join(' ');
  return String(line.text || '').trim();
}

/** "+ Deccox 2kg + Rumensin" — '' when none. */
export function additivesText(additives) {
  const names = (Array.isArray(additives) ? additives : [])
    .map(a => a && String(a.additive_name || a.additive_code || '').trim())
    .filter(Boolean);
  return names.length ? `+ ${names.join(' + ')}` : '';
}

/** "40 × 25kg bag — 10014208 Harvester 18 + Deccox" */
export function lineText(line) {
  if (!line) return '';
  const qty = quantityText(line.quantity, line.unit_weight_kg);
  const add = additivesText(line.additives);
  return [qty ? `${qty} —` : '', lineName(line) || 'No product', add].filter(Boolean).join(' ');
}

/** "On Mon 03/11/2026", "Between Mon 03/11/2026 and Fri 07/11/2026", ''. */
export function deliveryText(delivery) {
  if (!delivery || typeof delivery !== 'object') return '';
  if (delivery.type === 'date' && isIsoDay(delivery.date)) return `On ${dayText(delivery.date)}`;
  if (delivery.type === 'range' && isIsoDay(delivery.from) && isIsoDay(delivery.to)) {
    return `Between ${dayText(delivery.from)} and ${dayText(delivery.to)}`;
  }
  return '';
}

/** "Weekly on Wednesdays", "Every 2 weeks", "Every month", ''. */
export function scheduleText(schedule) {
  if (!schedule || typeof schedule !== 'object') return '';
  if (schedule.schedule_type === 'weekly') {
    const day = WEEKDAYS.find(d => d.value === Number(schedule.day_of_week));
    return day ? `Weekly on ${day.plural}` : 'Weekly';
  }
  if (schedule.schedule_type === 'interval') {
    const n = Number(schedule.interval_value);
    const type = INTERVAL_TYPES.find(t => t.value === schedule.interval_type);
    if (!type || !Number.isInteger(n) || n < 1) return '';
    return n === 1 ? `Every ${type.one}` : `Every ${n} ${type.value}`;
  }
  return '';
}

/** "Request #12" */
export const requestRef = (id) => (id ? `Request #${id}` : 'Request');

/** When something happened: the time today, else the date and time (London). */
export function whenText(at, now = new Date()) {
  if (!at) return '';
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return '';
  return londonToday(d) === londonToday(now) ? formatTimeLondon(d) : formatDateTimeLondon(d);
}

/**
 * Where a request stands, in words, with a tone for its chip:
 *   open       Sent
 *   opened     Opened by Jade Games at 14:02
 *   completed  Done — SO-D10871
 *   closed     Closed: <reason>
 *   withdrawn  Withdrawn
 */
export function statusOf(request, now = new Date()) {
  const r = request || {};
  switch (r.status) {
    case 'open':
      return { key: 'open', tone: 'info', text: 'Sent' };
    case 'opened': {
      const when = whenText(r.opened_at, now);
      return { key: 'opened', tone: 'warning', text: `Opened by ${r.opened_by_name || 'the office'}${when ? ` at ${when}` : ''}` };
    }
    case 'completed': {
      const ref = r.produced_order_number || (r.produced_standing_order_id ? `standing order #${r.produced_standing_order_id}` : '');
      return { key: 'completed', tone: 'success', text: ref ? `Done — ${ref}` : 'Done' };
    }
    case 'closed':
      return { key: 'closed', tone: 'muted', text: `Closed: ${String(r.close_reason || '').trim() || 'no reason given'}` };
    case 'withdrawn':
      return { key: 'withdrawn', tone: 'muted', text: 'Withdrawn' };
    default:
      return { key: String(r.status || ''), tone: 'muted', text: String(r.status || '') };
  }
}

/** Only while the office has not opened it — once opened, it is the office's. */
export const canWithdraw = (request) => !!request && request.status === 'open';

/**
 * Why an order cannot have a change asked for, or '' when it can.
 * Reads { order_number, is_archived, invoiced_status, has_live_invoice }.
 */
export function orderAmendBlock(order) {
  if (!order) return 'No order';
  const no = order.order_number || 'This order';
  if (order.is_archived) return `${no} is archived — it can't be changed`;
  if (order.has_live_invoice || String(order.invoiced_status || '').toLowerCase() === 'invoiced') {
    return `${no} is invoiced — ask the office for a credit note or a new order instead`;
  }
  return '';
}

/**
 * A request as sent, checked and tidied. `today` is London's ('YYYY-MM-DD');
 * `keepDate` is a past date an amendment may keep (the order's own).
 * @returns {{ request: object|null, errors: Array<{ field, text }> }}
 */
export function normaliseRequest(body, { today = londonToday(), keepDate = null } = {}) {
  const b = body && typeof body === 'object' ? body : {};
  const errors = [];
  const err = (field, text) => errors.push({ field, text });

  const kind = String(b.kind || '');
  if (!REQUEST_KINDS.includes(kind)) {
    err('kind', 'Say what is being asked for — an order, a standing order, or a change to one');
    return { request: null, errors };
  }
  const standing = isStandingKind(kind);

  const accountId = int(b.account_id);
  if (!isAmendKind(kind) && !accountId) err('account_id', 'Choose the customer');
  const orderId = kind === 'amend_order' ? int(b.order_id) : null;
  if (kind === 'amend_order' && !orderId) err('order_id', 'Which order is the change to?');
  const standingOrderId = kind === 'amend_standing_order' ? int(b.standing_order_id) : null;
  if (kind === 'amend_standing_order' && !standingOrderId) err('standing_order_id', 'Which standing order is the change to?');

  const rawLines = Array.isArray(b.lines) ? b.lines : [];
  const lines = [];
  if (!rawLines.length) err('lines', 'Add a product');
  if (rawLines.length > MAX_LINES) err('lines', `No more than ${MAX_LINES} products on one request`);
  if (standing && rawLines.length > 1) {
    err('lines', 'A standing order is for one product — send another request for a second one');
  }
  rawLines.slice(0, MAX_LINES).forEach((raw, i) => {
    const l = raw && typeof raw === 'object' ? raw : {};
    const label = rawLines.length > 1 ? `Product ${i + 1}` : 'The product';
    const itemId = int(l.item_id);
    const text = trimTo(l.text, LINE_TEXT_MAX);
    if (!itemId && !text) err(`lines.${i}.item`, `${label}: choose one, or type what it is`);
    const q = blank(l.quantity) ? NaN : Number(l.quantity);
    if (!Number.isFinite(q) || q <= 0) err(`lines.${i}.quantity`, `${label}: the quantity must be more than 0`);
    let kg = null;
    if (blank(l.unit_weight_kg)) {
      err(`lines.${i}.unit`, `${label}: choose its unit (bulk, a tote, a bag, or None for a charge with no weight)`);
    } else {
      kg = Number(l.unit_weight_kg);
      if (!Number.isFinite(kg) || kg < 0 || kg > 2000) {
        err(`lines.${i}.unit`, `${label}: its unit isn't one we know`);
        kg = null;
      }
    }
    const seen = new Set();
    const additives = (Array.isArray(l.additives) ? l.additives : [])
      .map(a => (a && typeof a === 'object' ? a : {}))
      .map(a => ({
        additive_id: int(a.additive_id),
        additive_code: trimTo(a.additive_code, 50) || null,
        additive_name: trimTo(a.additive_name, 200) || null
      }))
      .filter(a => a.additive_id && !seen.has(a.additive_id) && seen.add(a.additive_id));
    if (additives.length > MAX_ADDITIVES) err(`lines.${i}.additives`, `${label}: no more than ${MAX_ADDITIVES} additives`);
    lines.push({
      item_id: itemId,
      text: itemId ? null : (text || null),
      quantity: Number.isFinite(q) ? Math.round(q * 10000) / 10000 : null,
      unit_weight_kg: kg,
      additives: additives.slice(0, MAX_ADDITIVES),
      note: trimTo(l.note, LINE_NOTE_MAX) || null
    });
  });

  let delivery = null;
  if (!standing) {
    const d = b.delivery && typeof b.delivery === 'object' ? b.delivery : null;
    const notPast = (iso) => iso >= today || (keepDate && iso === keepDate);
    if (!d || !['date', 'range'].includes(d.type)) {
      err('delivery', 'Choose the delivery date, or the dates it can go between');
    } else if (d.type === 'date') {
      if (!isIsoDay(d.date)) err('delivery', 'Choose the delivery date');
      else if (!notPast(d.date)) err('delivery', 'The delivery date is in the past');
      else delivery = { type: 'date', date: d.date };
    } else {
      if (!isIsoDay(d.from) || !isIsoDay(d.to)) err('delivery', 'Choose both dates it can go between');
      else if (d.to < d.from) err('delivery', 'The second date is before the first');
      else if (d.from < today) err('delivery', 'The first date is in the past');
      else if (daysBetween(d.from, d.to) > RANGE_MAX_DAYS) err('delivery', `The dates are more than ${RANGE_MAX_DAYS} days apart — choose a narrower window`);
      else delivery = { type: 'range', from: d.from, to: d.to };
    }
  }

  let schedule = null;
  if (standing) {
    const s = b.schedule && typeof b.schedule === 'object' ? b.schedule : null;
    if (!s || !['weekly', 'interval'].includes(s.schedule_type)) {
      err('schedule', 'Choose how often — weekly on a day, or every so many days, weeks or months');
    } else if (s.schedule_type === 'weekly') {
      const day = Number(s.day_of_week);
      if (!WEEKDAYS.some(w => w.value === day)) err('schedule', 'Choose the day of the week (Monday to Friday)');
      else schedule = { schedule_type: 'weekly', day_of_week: day, interval_value: null, interval_type: null };
    } else {
      const n = Number(s.interval_value);
      if (!Number.isInteger(n) || n < 1 || n > INTERVAL_MAX) err('schedule', `How often — a whole number from 1 to ${INTERVAL_MAX}`);
      else if (!INTERVAL_TYPES.some(t => t.value === s.interval_type)) err('schedule', 'Days, weeks or months?');
      else schedule = { schedule_type: 'interval', day_of_week: null, interval_value: n, interval_type: s.interval_type };
    }
  }

  const notes = trimTo(b.notes, NOTES_MAX) || null;
  if (!blank(b.notes) && String(b.notes).trim().length > NOTES_MAX) err('notes', `Notes are limited to ${NOTES_MAX} characters`);

  if (errors.length) return { request: null, errors };
  return {
    request: {
      kind,
      account_id: accountId,
      order_id: orderId,
      standing_order_id: standingOrderId,
      lines,
      delivery,
      schedule,
      notes
    },
    errors
  };
}

/** The errors as one sentence for an alert. */
export function errorsText(errors) {
  const list = (errors || []).map(e => e.text).filter(Boolean);
  return list.join('. ') + (list.length ? '.' : '');
}

const additiveKey = (additives) => (Array.isArray(additives) ? additives : [])
  .map(a => Number(a && a.additive_id) || String((a && (a.additive_code || a.additive_name)) || ''))
  .sort().join(',');

/**
 * For a change: what it was → what is asked, line by line, in words (the
 * delivery or schedule, then each product). The snapshot is what the order /
 * standing order was when the rep asked.
 */
export function amendChanges(request) {
  const r = request || {};
  const s = r.snapshot || {};
  const out = [];
  if (r.kind === 'amend_order') {
    const was = s.delivery_date ? `On ${dayText(String(s.delivery_date).slice(0, 10))}` : 'No delivery date';
    const now = deliveryText(r.delivery);
    if (now && now !== was) out.push(`Delivery: ${was} → ${now}`);
  }
  if (r.kind === 'amend_standing_order') {
    const was = scheduleText(s.schedule);
    const now = scheduleText(r.schedule);
    if (now && now !== was) out.push(`Schedule: ${was || 'not set'} → ${now}`);
  }
  const before = Array.isArray(s.lines) ? s.lines : [];
  const after = Array.isArray(r.lines) ? r.lines : [];
  const n = Math.max(before.length, after.length);
  for (let i = 0; i < n; i += 1) {
    const a = before[i];
    const b = after[i];
    const label = n > 1 ? `Product ${i + 1}` : 'Product';
    if (a && !b) { out.push(`Take off: ${lineText(a)}`); continue; }
    if (!a && b) { out.push(`Add: ${lineText(b)}`); continue; }
    if (lineName(a) !== lineName(b)) out.push(`${label}: ${lineName(a) || 'No product'} → ${lineName(b) || 'No product'}`);
    const qa = quantityText(a.quantity, a.unit_weight_kg);
    const qb = quantityText(b.quantity, b.unit_weight_kg);
    if (qa !== qb) out.push(`${label} quantity: ${qa || 'not set'} → ${qb || 'not set'}`);
    if (additiveKey(a.additives) !== additiveKey(b.additives)) {
      out.push(`${label} additives: ${additivesText(a.additives) || 'none'} → ${additivesText(b.additives) || 'none'}`);
    }
  }
  // The rep's notes are shown on their own wherever a request is, so they
  // are not repeated here.
  return out;
}

/**
 * What a new customer order starts from: the customer, the delivery date (a
 * range's first day — the range itself goes in the notes, so it is not lost),
 * the notes (with any line notes), and each line's facts.
 */
export function orderPrefill(request) {
  const r = request || {};
  const notes = [];
  if (r.notes) notes.push(String(r.notes).trim());
  if (r.delivery && r.delivery.type === 'range') {
    notes.push(`Delivery wanted between ${dayText(r.delivery.from)} and ${dayText(r.delivery.to)}.`);
  }
  (Array.isArray(r.lines) ? r.lines : []).forEach((l) => {
    if (l && l.note) notes.push(`${lineName(l) || 'Product'}: ${l.note}`);
  });
  const deliveryDate = r.delivery
    ? (r.delivery.type === 'date' ? r.delivery.date : r.delivery.from)
    : null;
  return {
    account_id: r.account_id || null,
    delivery_date: deliveryDate || null,
    notes: notes.filter(Boolean).join('\n'),
    lines: (Array.isArray(r.lines) ? r.lines : []).map(l => ({
      item_id: l.item_id || null,
      item_code: l.item_code || null,
      item_name: l.item_name || null,
      sales_text: l.item_sales_description || l.item_name || l.text || '',
      text: l.text || null,
      quantity: l.quantity,
      unit_weight_kg: l.unit_weight_kg,
      additives: Array.isArray(l.additives) ? l.additives : []
    }))
  };
}

/** What a new standing order starts from (one product: the first line). */
export function standingOrderPrefill(request) {
  const r = request || {};
  const line = (Array.isArray(r.lines) ? r.lines : [])[0] || {};
  const s = r.schedule || {};
  const notes = [r.notes, line.note, line.item_id ? '' : (line.text ? `Asked for: ${line.text}` : '')]
    .map(v => String(v || '').trim()).filter(Boolean).join('\n');
  return {
    account_id: r.account_id || null,
    products: line.item_id ? [{ item_id: line.item_id, additives: Array.isArray(line.additives) ? line.additives : [] }] : [],
    quantity: line.quantity ?? '',
    schedule_type: s.schedule_type || 'weekly',
    day_of_week: s.day_of_week || 1,
    interval_value: s.interval_value || '',
    interval_type: s.interval_type || 'days',
    notes
  };
}

/** A standing order's schedule as a request's (for a change's starting point). */
export function scheduleOf(standingOrder) {
  const so = standingOrder || {};
  if (so.schedule_type === 'interval') {
    return { schedule_type: 'interval', day_of_week: null, interval_value: Number(so.interval_value) || null, interval_type: so.interval_type || 'days' };
  }
  return { schedule_type: 'weekly', day_of_week: Number(so.day_of_week) || 1, interval_value: null, interval_type: null };
}
